// See Documentation: ../../../Document/InsightsMap.md
import mapboxgl from "mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax

import { QI_LAYER_IDS } from "../constants/queueInsightsConstants";

import Layer, { FilterCategory, Filter } from "../../types/Layer";

import { QIVisibleFeatures } from "../../types/types";

import useMapboxSources from "../hooks/useMapboxSources";

import InsightsMap from "./InsightsMap";
import { createQueueIsoLayerFilter } from "../../utils/queue-iso-utils";
import { useRef, useCallback, useEffect } from "react";

import { useQueueInsights } from "../../contexts/QueueInsightsContext";

type QueueInsightsMapProps = {
    mapboxAccessToken: string;
    theme: "light" | "dark";
    isPageExpanded: boolean;
};

const QueueInsightsMap: React.FC<QueueInsightsMapProps> = ({
    mapboxAccessToken,
    theme,
    isPageExpanded,
}) => {
    mapboxgl.accessToken = mapboxAccessToken;
    const {
        updateVisibleFeatures,
        updateSelectedArea,
        clearSelectedArea,
        currentISOFilter,
        selectedGenerationTypes,
        selectedStateTypes,
        selectedCountyTypes,
        selectedClusterTypes,
        mapInstanceRef,
    } = useQueueInsights();

    const { mapboxSource } = useMapboxSources();

    const currentFiltersRef = useRef({
        isoFilter: currentISOFilter,
        generationTypes: selectedGenerationTypes,
        stateTypes: selectedStateTypes,
        countyTypes: selectedCountyTypes,
        clusterTypes: selectedClusterTypes,
    });

    const mapReadyRef = useRef(false);

    // Apply filters whenever any selection changes
    useEffect(() => {
        currentFiltersRef.current = {
            isoFilter: currentISOFilter,
            generationTypes: selectedGenerationTypes,
            stateTypes: selectedStateTypes,
            countyTypes: selectedCountyTypes,
            clusterTypes: selectedClusterTypes,
        };
        const map = mapInstanceRef?.current;
        if (!map || !mapReadyRef.current) return;
        
        const layerId = QI_LAYER_IDS.QUEUE_ISO_LAYER_ID;
        if (!map.getLayer(layerId)) return;

        const filterExpr = createQueueIsoLayerFilter(currentFiltersRef.current);
        map.setFilter(layerId, filterExpr as any);
    }, [
        currentISOFilter,
        selectedGenerationTypes,
        selectedStateTypes,
        selectedCountyTypes,
        selectedClusterTypes,
        mapInstanceRef,
    ]);

    // Runs once when InsightsMap is loaded
    const handleMapReady = useCallback(
        (m: mapboxgl.Map) => {
            mapInstanceRef.current = m;
            mapReadyRef.current = true; // Mark map as ready
            const layerId = QI_LAYER_IDS.QUEUE_ISO_LAYER_ID;
            const map = mapInstanceRef.current;
            if (!map || !map.getLayer(layerId)) return;
            const filterExpr = createQueueIsoLayerFilter(
                currentFiltersRef.current
            );
            map.setFilter(layerId, filterExpr as any);
        },
        [mapInstanceRef]
    );

    // Step 1. define layer settings
    function initializeTransmissionInsightsLayers(): Layer[] {
        const initialLayers = [
            new FilterCategory({
                id: QI_LAYER_IDS.QUEUE_ISO_LAYER_ID,
                visible: true,
                label: "ISO Regions",
                subFilters: [
                    new Filter({
                        id: "caiso",
                        label: "CAISO",
                        visible: true,
                        color: "#a3e635",
                        symbolType: "other",
                    }),
                    new Filter({
                        id: "ercot",
                        label: "ERCOT",
                        visible: true,
                        color: "#facc15",
                        symbolType: "other",
                    }),
                    new Filter({
                        id: "miso",
                        label: "MISO",
                        visible: true,
                        color: "#60a5fa",
                        symbolType: "other",
                    }),
                    new Filter({
                        id: "nyiso",
                        label: "NYISO",
                        visible: true,
                        color: "#c084fc",
                        symbolType: "other",
                    }),
                    new Filter({
                        id: "pjm",
                        label: "PJM",
                        visible: true,
                        color: "#f97316",
                        symbolType: "other",
                    }),
                    new Filter({
                        id: "spp",
                        label: "SPP",
                        visible: true,
                        color: "#34d399",
                        symbolType: "other",
                    }),
                ],
                layerSettings: {
                    type: "circle",
                    source: "queue-iso-source",
                    paint: {
                        "circle-color": "red",
                        "circle-opacity": 1,
                        "circle-stroke-color": "black",
                        "circle-radius": 2,
                    },
                },
                filterProperty: "ISO",
            }),
        ];
        initialLayers.forEach((layer) => {
            if (layer.isCheckable) {
                layer.assignParents();
            }
        });
        return initialLayers;
    }

    const initialLayers: Layer[] = initializeTransmissionInsightsLayers();

    // Step 2. defining layer-feature mappings
    const layerIdTypeMap = new Map([
        [QI_LAYER_IDS.QUEUE_ISO_LAYER_ID, QIVisibleFeatures.iso],
    ]);

    return (
        <div
            style={{
                width: "50%",
            }}
        >
            <>
                {mapboxSource ? (
                    <InsightsMap
                        mapboxAccessToken={mapboxAccessToken}
                        theme={theme}
                        isPageExpanded={isPageExpanded}
                        updateVisibleFeatures={updateVisibleFeatures}
                        updateSelectedArea={updateSelectedArea}
                        clearSelectedArea={clearSelectedArea}
                        mapboxSource={mapboxSource}
                        config={{
                            mapLayerConfig: initialLayers,
                            mapLayerIds: layerIdTypeMap,
                            mouseHandlers: [],
                        }}
                        onMapReady={handleMapReady}
                    />
                ) : (
                    <p> loading </p>
                )}
            </>
        </div>
    );
};
export default QueueInsightsMap;
